import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import dayjs from '../config/dayjs';
import { useCheque, useAuth, useNotification } from '../context';
import * as cambioFechaService from '../services/cambioFechaService';

const CheckDateChangesPage = () => {
  const navigate = useNavigate();
  const { cheques, loading, fetchCheques } = useCheque();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Formulario de cambio de fecha
  const [formData, setFormData] = useState({
    nuevaFecha: null,
    motivo: '',
    solicitante: '',
  });

  const [formErrors, setFormErrors] = useState({});

  // Cargar cheques al montar
  useEffect(() => {
    fetchCheques({ estado: 'pendiente' });
  }, []);

  // Filtrar solo cheques pendientes (disponibles para cambio de fecha)
  const chequesPendientes = useMemo(() => {
    return cheques.filter(cheque => cheque.estado === 'pendiente');
  }, [cheques]);

  // Paginación
  const paginatedCheques = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return chequesPendientes.slice(startIndex, startIndex + rowsPerPage);
  }, [chequesPendientes, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (chequeId) => {
    navigate(`/cheques/${chequeId}`);
  };

  const handleOpenDialog = (cheque) => {
    // Verificar que el usuario sea contador o administrador
    if (user?.rol !== 'contador' && user?.rol !== 'administrador') {
      showError('Solo los Contadores y Administradores pueden cambiar fechas de cheques');
      return;
    }

    setSelectedCheque(cheque);
    setFormData({
      nuevaFecha: null,
      motivo: '',
      solicitante: '',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCheque(null);
    setFormData({
      nuevaFecha: null,
      motivo: '',
      solicitante: '',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nuevaFecha) {
      errors.nuevaFecha = 'La nueva fecha es requerida';
    } else {
      const nuevaFecha = dayjs(formData.nuevaFecha);
      const fechaActual = dayjs(selectedCheque.fecha_pago);

      if (nuevaFecha.isSame(fechaActual, 'day')) {
        errors.nuevaFecha = 'La nueva fecha debe ser diferente a la actual';
      }
    }

    if (!formData.motivo || formData.motivo.trim() === '') {
      errors.motivo = 'El motivo es requerido';
    } else if (formData.motivo.trim().length < 10) {
      errors.motivo = 'El motivo debe tener al menos 10 caracteres';
    }

    if (!formData.solicitante || formData.solicitante.trim() === '') {
      errors.solicitante = 'El solicitante es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showWarning('Por favor, complete todos los campos requeridos');
      return;
    }

    try {
      setSubmitting(true);

      const data = {
        cheque_id: selectedCheque.id,
        fecha_nueva: dayjs(formData.nuevaFecha).format('YYYY-MM-DD'),
        motivo: formData.motivo.trim(),
        solicitante: formData.solicitante.trim(),
      };

      await cambioFechaService.requestDateChange(data);

      showSuccess('Cambio de fecha registrado exitosamente');
      handleCloseDialog();

      // Recargar cheques para reflejar el cambio
      fetchCheques({ estado: 'pendiente' });
    } catch (error) {
      console.error('Error al cambiar fecha:', error);
      const errorMessage = error.response?.data?.error || 'Error al cambiar la fecha del cheque';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return '-';
    }
  };

  const getClienteName = (cheque) => {
    if (typeof cheque.cliente === 'object' && cheque.cliente?.nombre) {
      return cheque.cliente.nombre;
    }
    return '-';
  };

  const getBancoNombre = (cheque) => {
    if (typeof cheque.banco === 'object' && cheque.banco?.nombre) {
      return cheque.banco.nombre;
    }
    return '-';
  };

  // Verificar si el usuario tiene permiso
  const hasPermission = user?.rol === 'contador' || user?.rol === 'administrador';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">
            Cambios de Fecha
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cheques pendientes disponibles para cambio de fecha
          </Typography>
        </Box>

        {/* Alerta de permisos */}
        {!hasPermission && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Solo los usuarios con rol Contador o Administrador pueden realizar cambios de fecha.
          </Alert>
        )}

        {/* Tabla de cheques */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Número de Cheque</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Fecha de Pago</TableCell>
                  <TableCell>Estado</TableCell>
                  {hasPermission && <TableCell align="center">Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={hasPermission ? 8 : 7} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedCheques.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hasPermission ? 8 : 7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        No se encontraron cheques pendientes
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCheques.map((cheque, index) => (
                    <TableRow
                      key={cheque.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        <Typography variant="body2" fontWeight="medium">
                          {cheque.numero_cheque}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        {getClienteName(cheque)}
                      </TableCell>
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        {getBancoNombre(cheque)}
                      </TableCell>
                      <TableCell align="right" onClick={() => handleRowClick(cheque.id)}>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(cheque.monto)}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        {formatDate(cheque.fecha_pago)}
                      </TableCell>
                      <TableCell onClick={() => handleRowClick(cheque.id)}>
                        <Chip
                          label="Pendiente"
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      {hasPermission && (
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(cheque);
                            }}
                            size="small"
                            title="Cambiar fecha de pago"
                          >
                            <EditCalendarIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={chequesPendientes.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </Card>

        {/* Diálogo de cambio de fecha */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Cambiar Fecha de Pago
          </DialogTitle>
          <DialogContent>
            {selectedCheque && (
              <Box sx={{ mt: 2 }}>
                {/* Información del cheque */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Información del Cheque
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Número:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCheque.numero_cheque}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Cliente:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {getClienteName(selectedCheque)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Monto:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatCurrency(selectedCheque.monto)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha Actual:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatDate(selectedCheque.fecha_pago)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Formulario */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Nueva Fecha de Pago *"
                      value={formData.nuevaFecha}
                      onChange={(newValue) =>
                        setFormData({ ...formData, nuevaFecha: newValue })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.nuevaFecha,
                          helperText: formErrors.nuevaFecha,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Solicitante *"
                      value={formData.solicitante}
                      onChange={(e) =>
                        setFormData({ ...formData, solicitante: e.target.value })
                      }
                      error={!!formErrors.solicitante}
                      helperText={formErrors.solicitante || 'Nombre de quien solicita el cambio (cliente, contador, etc.)'}
                      placeholder="Ej: Juan Pérez - Cliente"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Motivo del Cambio *"
                      value={formData.motivo}
                      onChange={(e) =>
                        setFormData({ ...formData, motivo: e.target.value })
                      }
                      error={!!formErrors.motivo}
                      helperText={formErrors.motivo || 'Describa detalladamente el motivo del cambio de fecha (mínimo 10 caracteres)'}
                      placeholder="Ej: Solicitud del cliente por problemas de liquidez temporal"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
            >
              {submitting ? 'Procesando...' : 'Confirmar Cambio'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CheckDateChangesPage;
